<template>
  <div>
    <a-button type='text' size='small' @click='handleOpen'>
      <template #icon>
        <download-outlined></download-outlined>
      </template>
    </a-button>
    <a-modal
      width='800px'
      :visible='visible'
      :maskClosable='false'
      :confirmLoading='loading'
      cancelText='取消'
      :okText='`导出${state.selectedFileType}`'
      centered
      @ok='handleExport'
      @cancel='handleCancel'
    >
      <template #title>
        <div ref='modalTitleRef' style='width: 100%; cursor: move'>导出Excel</div>
      </template>
      <template #modalRender='{ originVNode }'>
        <div :style='transformStyle'>
          <component :is='originVNode' />
        </div>
      </template>
      <a-form :label-col='{ span: 4 }' :wrapper-col='{ span: 20 }'>
        <a-form-item label='文件名称:'>
          <a-input v-model:value='state.filename' size='small' />
        </a-form-item>
        <a-form-item label='文件类型:'>
          <a-radio-group v-model:value='state.selectedFileType'>
            <a-radio v-for='(item, index) in state.fileTypeList' :key='index' :value='item'>
              {{ item }}
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item label='导出列:'>
          <!--      todo:可选导出列-->
          <a-select size='default' v-model:value='state.selectedColumns' mode='multiple'>
            <a-select-option v-for='item in columnList' :key='item.key' :value='item.key'>
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <!--      todo:可选导出条目-->
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script lang='ts'>
import { computed, defineComponent, reactive, CSSProperties, ref, watch, watchEffect } from 'vue';
import { useDraggable } from '@vueuse/core';
import { exportJsonToExcel } from '@/utils/excel';
import { BookType } from 'xlsx';

type StateType = {
  selectedFileType: string;
  fileTypeList: string[];
  selectedColumns: string[] | unknown[];
  filename: string;
};
import { DownloadOutlined } from '@ant-design/icons-vue';
import { isNumber } from 'lodash';
import axios from 'axios';

export default defineComponent({
  name: 'exportExcelModal',
  props: {
    fileType: {
      type: String,
      default: () => 'xlsx',
    },
    columnList: {
      type: Array,
      default: () => [],
    },
    filename: {
      type: String,
      default: () => 'exportFile',
    },
    datasource: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    pageSize: {
      type: Number,
      default: 0,
    },
    current: {
      type: Number,
      default: 0,
    },
  },
  components: {
    DownloadOutlined,
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    const visible = ref<boolean>(false);
    const loading = ref<boolean>(false);
    const state = reactive<StateType>({
      selectedFileType: 'xlsx',
      filename: props.filename,
      selectedColumns: props.columnList
        .filter((item: any) => item.checked)
        .map((item: any) => {
          return item.key;
        }),
      fileTypeList: ['xlsx', 'csv', 'txt'],
    });

    const modalTitleRef: any = ref(null);
    const { x, y, isDragging } = useDraggable(modalTitleRef);
    const startX = ref<number>(0);
    const startY = ref<number>(0);
    const startedDrag = ref(false);
    const transformX = ref(0);
    const transformY = ref(0);
    const preTransformX = ref(0);
    const preTransformY = ref(0);
    const dragRect = ref({ left: 0, right: 0, top: 0, bottom: 0 });
    watch([x, y], () => {
      if (!startedDrag.value) {
        startX.value = x.value;
        startY.value = y.value;
        const bodyRect = document.body.getBoundingClientRect();
        const titleRect = modalTitleRef.value.getBoundingClientRect();
        dragRect.value.right = bodyRect.width - titleRect.width;
        dragRect.value.bottom = bodyRect.height - titleRect.height;
        preTransformX.value = transformX.value;
        preTransformY.value = transformY.value;
      }
      startedDrag.value = true;
    });
    watch(isDragging, () => {
      if (!isDragging) {
        startedDrag.value = false;
      }
    });
    watchEffect(() => {
      if (startedDrag.value) {
        transformX.value =
          preTransformX.value +
          Math.min(Math.max(dragRect.value.left, x.value), dragRect.value.right) -
          startX.value;
        transformY.value =
          preTransformY.value +
          Math.min(Math.max(dragRect.value.top, y.value), dragRect.value.bottom) -
          startY.value;
      }
    });
    const transformStyle = computed<CSSProperties>(() => {
      return {
        transform: `translate(${transformX.value}px, ${transformY.value}px)`,
      };
    });
    const handleOpen = () => {
      visible.value = true;
    };
    const handleCancel = () => {
      visible.value = false;
    };

    const column_dict: any = {};
    props.columns.forEach((item: any) => {
      column_dict[item.dataIndex ? item.dataIndex : item.key] = item;
    });

    // const slot_dict: any = {};
    // props.slotList.forEach((item: any) => {
    //   slot_dict[item.slotName] = item;
    // });

    const value_compute = (rowIndex: number, text: any, record: any, key: string) => {
      if (column_dict[key]?.dataIndex == 'index') {
        return rowIndex + 1 + props.pageSize * (props.current - 1);
      } else if (column_dict[key]?.content) {
        return column_dict[key]?.content(record, text);
      } else if (column_dict[key]?.cellComponent == 'list') {
        let str: any = '';
        if (text && text.length > 0) {
          text.forEach((item: any, index: any) => {
            if(index == text.length - 1){
              str += item.name;
            }else{
              str += item.name + ', ';
            }
          });
        }
        return str;
      } else if (column_dict[key]?.cellComponent) {
        return text;
      } else {
        return text;
      }
    };

    const handleExport = (e: Event) => {
      loading.value = true;
      e.preventDefault();

      const selectedColumns = props.columnList
        .map((item: any) => {
          if (state.selectedColumns.indexOf(item.key) > -1) {
            return item;
          } else {
            return null;
          }
        })
        .filter(item => {
          if (item) {
            return true;
          }
        });

      console.error('开始：' + new Date().getTime());
      const excel_data = props.datasource.map((item: any, rowIndex: number) =>
        selectedColumns.map((v: any) => value_compute(rowIndex, item[v.key], item, v.key)),
      );
      console.error('数据处理结束：' + new Date().getTime());
      if (state.selectedFileType == 'xlsx(服务端)') {
        excel_data.unshift(selectedColumns.map((item: any) => item.label));
        let url = '/api/sys/download_excel_by_data';
        if (process.env.NODE_ENV == 'production') {
          url = '/sys/download_excel_by_data';
        }
        axios
          .post(
            url,
            { filename: state.filename, data: excel_data },
            {
              responseType: 'blob',
            },
          )
          .then((res: any) => {
            var blob = res.data;
            // FileReader主要用于将文件内容读入内存
            var reader = new FileReader();
            reader.readAsDataURL(blob);
            // onload当读取操作成功完成时调用
            reader.onload = (e: any) => {
              var a = document.createElement('a');
              // 获取文件名fileName
              // var fileName = res.config["url"].split("/");
              // fileName = fileName[fileName.length - 1];
              // fileName = fileName.replace(/"/g, "");
              a.download = state.filename + '.xlsx';
              a.href = e.target.result;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              visible.value = false;
            };
          })
          .finally(() => {
            loading.value = false;
          });
      } else {
        exportJsonToExcel({
          data: excel_data,
          header: selectedColumns.map((item: any) => item.label),
          filename: state.filename,
          bookType: state.selectedFileType as BookType,
          autoWidth: false,
        });
        loading.value = false;
        visible.value = false;
        console.error('导出结束：' + new Date().getTime());
      }
    };

    return {
      props,
      state,
      visible,
      loading,
      modalTitleRef,
      transformStyle,
      handleOpen,
      handleCancel,
      handleExport,
    };
  },
});
</script>
